import { create } from 'zustand';

// ----------------------------------------------------------------------

type FilterValue = string | number | boolean | null | undefined;

interface FilterState {
  [key: string]: FilterValue;
}

/**
 * Extrai a chave de entidade do pathname da URL.
 *
 * Regra: primeiro segmento após /dashboard/.
 *
 * Exemplos:
 *   /dashboard/invoices         → "invoices"
 *   /dashboard/invoices/123     → "invoices"
 *   /dashboard/faq/categories   → "faq"
 *   /dashboard/coupons          → "coupons"
 */
export function getEntityKeyFromPath(pathname: string): string | null {
  if (/^\/dashboard\/notifications\/logs(\/|$)/.test(pathname)) {
    return 'notification-logs';
  }

  if (/^\/dashboard\/notifications\/segments(\/|$)/.test(pathname)) {
    return 'segments';
  }

  const match = pathname.match(/^\/dashboard\/([^/]+)/);
  return match ? match[1] : null;
}

interface TableStateStore {
  lastActiveEntityKey: string | null;
  entityFilterStates: Record<string, FilterState>;
  entityResetFunctions: Record<string, () => void>;

  setLastActiveEntityKey: (key: string) => void;
  registerEntityReset: (key: string, resetFn: () => void) => void;
  getEntityFilterState: (key: string) => FilterState;
  mergeEntityFilterState: (key: string, filters: FilterState) => void;
  clearEntityFilterState: (key: string) => void;
}

// ----------------------------------------------------------------------

export const useTableStateStore = create<TableStateStore>((set, get) => ({
  lastActiveEntityKey: null,
  entityFilterStates: {},
  entityResetFunctions: {},

  setLastActiveEntityKey: (key: string) => set({ lastActiveEntityKey: key }),

  registerEntityReset: (key: string, resetFn: () => void) =>
    set((state) => ({
      entityResetFunctions: { ...state.entityResetFunctions, [key]: resetFn },
    })),

  getEntityFilterState: (key: string) => get().entityFilterStates[key] ?? {},

  mergeEntityFilterState: (key: string, filters: FilterState) =>
    set((state) => ({
      entityFilterStates: {
        ...state.entityFilterStates,
        [key]: { ...(state.entityFilterStates[key] ?? {}), ...filters },
      },
    })),

  clearEntityFilterState: (key: string) =>
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _removed, ...rest } = state.entityFilterStates;
      return { entityFilterStates: rest };
    }),
}));
